<template>
    <BaseModal :title="groupId ? 'Edit Group' : 'Add Group'" @close="closeModal" :visible="true">
        <form @submit.prevent="onSubmit">
            <div class="rlms-flex rlms-flex-col rlms-gap-10">
                <div class="pp-body-scroll">
                    <div class="rlms-row rlms-row-responsive">
                        <div class="rlms-col-1">
                            <label for="group_name" class="rlms-fw-500">Name</label>
                            <input type="text" placeholder="Name" id="group_name" v-model.trim="name" />
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && nameError">{{
                                nameError }}</span>
                        </div>
                        <div class="rlms-col-1">
                            <label for="group_description" class="rlms-fw-500">Description</label>
                            <textarea type="text" placeholder="Description..." v-model.trim="description" id="group_description" rows="4"></textarea>
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && descriptionError">{{ descriptionError }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <template #footer>
            <div class="pp-footer rlms-flex rlms-justify-between" style="padding: 0;">
                <button class="rlms-btn rlms-btn-outline-primary p-cancel-btn" type="button" id="cancel-org-modal"
                    @click="closeModal">Cancel</button>
                <button type="submit" class="rlms-btn rlms-btn-primary" id="save-org-btn"
                    @click="onSubmit">Save</button>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { ref, watch } from 'vue';
import BaseModal from './BaseModal.vue';
import { useForm, useField } from 'vee-validate';
import * as yup from 'yup';
import axios from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { showToast } from '@/utils/swal';
import { handleError } from '@/utils/handleError';

const loader = useLoaderStore();

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);

// eslint-disable-next-line
const props = defineProps({
    organizationId: {
        type: Number,
        required: true,
    },
    group: {
        type: Object,
        default: null
    }
});

if (!props.organizationId || typeof props.organizationId !== 'number') {
    throw new Error('orgId prop is required');
}

const groupId = ref(null);
const showModal = ref(true);
const submitting = ref(false);
const submitted = ref(false);

const closeModal = () => {
    showModal.value = false;
    emit('close');
};

// Replace this with props or API fetched user
const formSchema = yup.object({
    name: yup.string().required('Please enter group name.'),
    description: yup.string().optional(),
});

// Setup form with schema
const { handleSubmit, setErrors, setFieldValue } = useForm({
    validationSchema: formSchema
})

// Fields
const { value: name, errorMessage: nameError } = useField('name');
const { value: description, errorMessage: descriptionError } = useField('description');

// Submit handler
const onSubmit = async () => {
    submitted.value = true;

    await handleSubmit(async (values) => {
        submitting.value = true;

        try {
            loader.show();

            if (groupId.value) {
                await axios.put(`/organizations/${props.organizationId}/groups/${groupId.value}`, { ...values });
            } else {
                await axios.post(`/organizations/${props.organizationId}/groups`, { ...values });
            }

            showToast({
                icon: 'success',
                title: 'Great! ',
                text: (groupId.value) ? 'Group updated successfully!' : 'Group created successfully!',
            });

            emit('saved');
        } catch (error) {
            if (error?.response?.status === 422) {
                const serverErrors = error?.response?.data?.errors || {};
                const fieldErrors = {};

                Object.keys(serverErrors).forEach(key => {
                    fieldErrors[key] = serverErrors[key][0];
                });

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                } else {
                    handleError(error, true);
                }
            } else {
                handleError(error);
            }
        } finally {
            submitting.value = false;
            loader.hide();
        }
    })();
};

// Watch for prop change and populate form
watch(
    () => props.group,
    (group) => {
        if (group) {
            groupId.value = group.id || null;
            setFieldValue('name', group.name || '');
            setFieldValue('description', group.description || '');
        }
    },
    { immediate: true }
);
</script>
